import { registerComponentInterceptor } from '../componentInterceptor';
import DevHubSupportButton from '../common/DevHubSupportButton';
import MarketplaceDocsButton from '../common/MarketplaceDocsButton';
import {
  DevHubBox,
  devHubDynamicSvgIcon,
  DevHubGrid,
  DevHubIconButton,
} from '@devxdiscover/devhub-ui';
import { default as React, useContext, useEffect, useState } from 'react';
import { ConfigHelper, Feature } from '@devxdiscover/devhub-common';
import { ConfigFeatureCheck } from '../../util/ConfigFeatureCheck.tsx';
import {
  useGroupMembership,
  UserContext,
} from '../../contexts/UserContext.tsx';
import { usePoll } from '../../util/usePoll.ts';
import { cacheDelete, cacheGet, cacheSet } from '../../util/persistedState.ts';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import { md5Hash } from '../../util/md5.mjs';

/**
 * Provides a custom layout for the SupportButton on the Entity page.
 */
export const CustomSupportButton = () => {
  const { login, hasGroupMembership } = useContext(UserContext);
  const repoName =
    ConfigHelper.instance.config.getOptionalString('app.templateRepository') ??
    'devhub-templates';
  const orgName =
    ConfigHelper.instance.config.getOptionalString('app.templateOrg') ??
    'comcast-devx-discover';
  const entityKey = md5Hash(`${orgName}/${repoName}`);
  const config = useApi(configApiRef);
  const onlyAdmins =
    ConfigHelper.instance.config.getOptionalBoolean(
      'ondemandRefresh.onlyAdmins',
    ) ?? true;
  const [canRefreshAdmin, setCanRefreshAdmin] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(
    () => !!cacheGet<string>(entityKey),
  );
  const [isCheckingAdminPermissions, setIsCheckingAdminPermissions] =
    useState(true);
  const {
    value: groupData,
    loading: isLoading,
    error: isError,
  } = useGroupMembership(login, ['[CHQ -- DevHub]']);

  const [pendingRunId, setPendingRunId] = useState<string | undefined>(
    () => cacheGet<string>(entityKey) ?? undefined,
  );
  const backendUrl = config.getString('backend.baseUrl');

  usePoll({
    enabled: !!pendingRunId,
    fn: async () => {
      const response = await fetch(
        `${backendUrl}/api/fusionUtil/isRunning/${pendingRunId}`,
      );
      if (!response.ok) throw new Error(response.statusText);
      const { isRunning } = await response.json();
      return !isRunning;
    },
    onDone: () => {
      cacheDelete(entityKey);
      setPendingRunId(undefined);
      setIsRefreshing(false);
    },
  });
  useEffect(() => {
    if (groupData && hasGroupMembership(groupData, '[CHQ -- DevHub]')) {
      setIsCheckingAdminPermissions(false);
      setCanRefreshAdmin(true);
    }
    if (isLoading) {
      setIsCheckingAdminPermissions(true);
      return;
    }

    if (isError) {
      setCanRefreshAdmin(false);
      setIsCheckingAdminPermissions(false);
      return;
    }
  }, [groupData, hasGroupMembership, isLoading, isError]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const response = await fetch(
        `${backendUrl}/api/fusionUtil/import/repo/${orgName}/${repoName}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            repoName,
            orgName,
            kind: 'template',
          }),
        },
      );

      if (!response.ok) {
        throw new Error(`Failed to refresh: ${response.statusText}`);
      }
      const responseData = await response.json();
      cacheSet(entityKey, responseData.runId, { minutes: 30 });
      setPendingRunId(responseData.runId);
    } catch (err) {
      setIsRefreshing(false);
    }
  };

  const isRunning = isRefreshing || !!pendingRunId;
  const canRefreshFlag =
    onlyAdmins && !isCheckingAdminPermissions && canRefreshAdmin;
  // Show the button if a refresh is in progress (disabled) OR the user has permission (enabled).
  // Hide it only when not running AND the user lacks permission (or check is still loading).
  const showRefreshButton = isRunning || canRefreshFlag;

  return (
    <DevHubGrid container justifyContent="flex-end" key="SupportButton">
      <MarketplaceDocsButton />
      <DevHubSupportButton />
      <ConfigFeatureCheck feature={Feature.ONDEMAND_REFRESH}>
        {showRefreshButton && (
          <DevHubBox mt={-1}>
            <DevHubIconButton
              onClick={handleRefresh}
              aria-label="refresh"
              title="Refresh"
              disabled={isRunning}
            >
              {devHubDynamicSvgIcon('Refresh')()}
            </DevHubIconButton>
          </DevHubBox>
        )}
      </ConfigFeatureCheck>
    </DevHubGrid>
  );
};

registerComponentInterceptor({
  canIntercept(_type: React.ElementType, props: any, _key: React.Key) {
    return props['data-testid'] === 'support-button' ? true : false;
  },
  performIntercept(_type: React.ElementType, _props: any, _key: React.Key) {
    return {
      element: <CustomSupportButton />,
    };
  },
});
